import { useTracker } from '@jetshop/core/analytics/Analytics';
import { trackCartCheckoutEvent } from '@jetshop/core/analytics/tracking';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import CartProvider from '@jetshop/core/components/Query/CartProvider';
import t from '@jetshop/intl';
import Image from '@jetshop/ui/Image/Image';
import { Price } from '@jetshop/ui/Price';
import theme from '@jetshop/ui/utils/theme';
import React from 'react';
import styled from 'react-emotion';
import { ReactComponent as Cross } from '../../../svg/Cross.svg';
import Button from '../../ui/Button';
import cartQuery from '../../Cart/CartQuery.gql';

const Container = styled('aside')`
  ${theme('above.sm')} {
    width: 320px;
  }
  ${theme('below.sm')} {
    width: 100%;
  }
  background-color: ${theme('colors.white')};
`;

const CheckoutButton = styled(Button)`
  font-size: 1.25rem;
  background: #79aa07;
  font-weight: 700;
  padding: ${theme('space.1')};
  margin: ${theme('space.1')};
  text-align: center;
`.withComponent('a');

const ProductImageWrapper = styled('div')`
  width: 5rem;
  margin-right: 1rem;
`;

const ProductCheckoutContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const Product = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin: ${theme('space.2')};
`;

const ProductDetails = styled('div')`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  color: black;
`;

const CloseButton = styled('button')`
  padding: 0;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  outline: none;
  border: none;
  background: #8f8f8f;
  margin-left: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    width: 0.6rem;
    height: 0.6rem;
  }
`;

const Header = styled('h3')`
  font-family: ${theme('fontFamilies.heavy')};
  font-size: ${theme('fontSizes.2')};
  margin-bottom: ${theme('space.1')};
`;

const ProductName = styled('p')`
  font-size: ${theme('fontSizes.0')};
`;

const Error = styled('p')`
  color: red;
  margin-top: ${theme('space.1')};
  font-size: ${theme('fontSizes.0')};
`;

const ProductToast = ({
  product,
  cart,
  closeToast,
  selectedVariation,
  error
}) => {
  const pricing = selectedVariation || product;
  const track = useTracker();

  return (
    <Container data-testid="product-toast">
      <ProductCheckoutContainer>
        <Product>
          {product.images.length > 0 && (
            <ProductImageWrapper>
              <Image
                src={product.images[0].url}
                sizes={80}
                aspect="3:4"
                alt={product.images[0].alt}
                quality={80}
              />
            </ProductImageWrapper>
          )}
          <ProductDetails>
            {error ? (
              <Header>{t('Failed adding to bag')}</Header>
            ) : (
              <Header>{t('Added to bag')}</Header>
            )}
            <ProductName>{product.name}</ProductName>
            {!error && (
              <Price
                price={pricing.price}
                recommendedPrice={pricing.recommendedPrice}
                previousPrice={pricing.previousPrice}
              />
            )}
            {error && (
              <Error>
                {t('An error occurred. Details:')}
                <ul>
                  {error.graphQLErrors && error.graphQLErrors.length > 0 ? (
                    error.graphQLErrors.map(({ message, locations, path }) => (
                      <li key={message}>{t(message)}</li>
                    ))
                  ) : (
                    <li>{t(error.message)}</li>
                  )}
                </ul>
              </Error>
            )}
          </ProductDetails>
          <CloseButton onClick={closeToast}>
            <Cross />
          </CloseButton>
        </Product>
        {cart && cart.externalCheckoutUrl && (
          <ChannelContext.Consumer>
            {({ selectedChannel }) => (
              <CheckoutButton
                href={`${cart.externalCheckoutUrl}&channelCountry=${selectedChannel.country.code}`}
                onClick={() => {
                  track(trackCartCheckoutEvent({ cart }));
                }}
              >
                {t('To checkout')}
              </CheckoutButton>
            )}
          </ChannelContext.Consumer>
        )}
      </ProductCheckoutContainer>
    </Container>
  );
};
const ProductToastWrapper = props => (
  <CartProvider query={cartQuery}>
    {({ data }) => <ProductToast {...props} cart={data && data.cart} />}
  </CartProvider>
);

export default ProductToastWrapper;
